import { useEffect, useRef, useState } from 'react';
import { startPhoneVerification, checkPhoneCode, ApiError, type User } from '../api.ts';
import { recordStep } from '../journey.ts';

/**
 * Who is asking, at the moment they ask.
 *
 * Henry: "My vision is you press search, it asks you for your name, email, and phone. Then a
 * code that verifies your phone". So this is not a sign-in page. The Scenario Desk is public;
 * a broker fills the file in, presses Search, and only then lands here - with the file held
 * by App, untouched, and run the moment the code is accepted.
 *
 * It is the original mockup's `verifyStep` machine (docs/reference/lending-hound-complete-3.jsx)
 * with the code moved to the server: form -> verify -> searching. The mockup generated its
 * six digits in the browser and printed them on screen under "Demo mode"; here Twilio holds
 * the code and nothing in this file ever knows it.
 *
 * ONE THING THE COPY MUST NOT DO is promise that a text was sent. The server answers "sent"
 * whether it sent, refused on a limit, or recognised a staff number, so that the endpoint
 * cannot be used to ask whether a phone belongs to a LenderHound broker. A screen that said
 * "we texted you" would be telling a broker something the server deliberately does not know
 * how to tell it - so it says "if this number can receive texts, a code is on its way".
 */

type Step = 'form' | 'verify' | 'searching';

/** Twilio Verify's default length. A service setting; change both together. */
const CODE_LENGTH = 6;
/** The server allows one code a minute per number, so the button waits the same minute. */
const RESEND_AFTER_MS = 60_000;

/**
 * What went wrong, in words a broker can act on.
 *
 * Same rule Login.tsx follows: only blame what the server blamed. A dropped connection is not
 * a wrong code, and a 500 is not a bad phone number.
 */
function gateError(err: unknown, step: Step): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_phone':
        return 'Enter a US or Canadian mobile number, like (310) 555-0100.';
      case 'invalid_request':
        return step === 'form'
          ? 'Check your name and email - both are needed, and the email has to be a real address.'
          : `Enter the ${CODE_LENGTH}-digit code from the text.`;
      case 'invalid_code':
        return 'That code did not match. Check the text, or send a new code.';
      case 'too_many_attempts':
        return 'Too many attempts from this address. Wait a few minutes and try again.';
      case 'phone_verification_unavailable':
        return 'Phone verification is not switched on yet, so a search cannot be verified '
             + 'right now. Your file is still here.';
    }
    if (err.detail) return err.detail;
    if (err.status >= 500) {
      return `The server hit an error (${err.status}). Nothing is wrong with your details - `
           + 'try again in a moment.';
    }
    return `Verification failed (${err.status} ${err.code}).`;
  }
  if (err instanceof TypeError) {
    return 'Cannot reach the server. It may be restarting - try again in a few seconds.';
  }
  return 'Verification failed for an unknown reason. Check the console for details.';
}

export default function VerifyGate({ onVerified, onCancel }: {
  /** Called with the signed-in user; App runs the held search from here. */
  onVerified: (user: User) => void;
  /** Back to the form, which kept everything typed into it. */
  onCancel: () => void;
}) {
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const codeRef = useRef<HTMLInputElement>(null);

  // A clock only while a countdown is running, so an idle gate does not re-render every second.
  useEffect(() => {
    if (step !== 'verify') return;
    const t = setInterval(() => {
      const at = Date.now();
      setNow(at);
      if (at >= resendAt) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [step, resendAt]);

  useEffect(() => { if (step === 'verify') codeRef.current?.focus(); }, [step]);

  async function sendCode(): Promise<void> {
    setBusy(true); setError('');
    try {
      await startPhoneVerification(name.trim(), email.trim(), phone.trim());
      // "Requested", not "sent": the server answers sent either way - see the header comment.
      void recordStep('code_requested');
      const at = Date.now();
      setNow(at); setResendAt(at + RESEND_AFTER_MS);
      setCode('');
      setStep('verify');
    } catch (err) {
      setError(gateError(err, 'form'));
    } finally {
      setBusy(false);
    }
  }

  async function verify(): Promise<void> {
    setBusy(true); setError('');
    try {
      const { user } = await checkPhoneCode(phone.trim(), code, name.trim(), email.trim());
      void recordStep('verified');
      setStep('searching');
      onVerified(user);
    } catch (err) {
      setError(gateError(err, 'verify'));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const card = { maxWidth: 480, margin: '0 auto 18px' } as const;

  if (step === 'searching') {
    return (
      <div className="card" role="status" style={{ ...card, textAlign: 'center', padding: '36px 20px' }}>
        <span className="spin" />
        <h2 style={{ margin: '14px 0 6px', fontSize: 22 }}>Verified</h2>
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}>
          Starting your search.
        </p>
      </div>
    );
  }

  if (step === 'verify') {
    const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));
    return (
      <form className="card" style={card}
            onSubmit={(e) => { e.preventDefault(); if (code.length === CODE_LENGTH) void verify(); }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 24 }}>Check your phone</h2>
        <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
          If <strong style={{ color: 'var(--ink)' }}>{phone.trim()}</strong> can receive texts,
          a {CODE_LENGTH}-digit code is on its way. Enter it to see your lender matches.
        </p>
        <label className="lbl" htmlFor="verify-code">Verification code</label>
        <input id="verify-code" ref={codeRef} className="finput" inputMode="numeric"
               autoComplete="one-time-code" maxLength={CODE_LENGTH} placeholder="000000"
               value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
               style={{ fontSize: 22, letterSpacing: '.3em', textAlign: 'center' }} />
        {error ? <div className="err-text" style={{ marginTop: 10 }}>{error}</div> : null}
        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 18 }}
                disabled={busy || code.length !== CODE_LENGTH}>
          {busy ? <><span className="spin" /> &nbsp;Checking…</> : 'Verify & see results'}
        </button>
        <div style={{
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          marginTop: 14,
        }}>
          <button type="button" className="btn btn-ghost"
                  onClick={() => { setError(''); setStep('form'); }}>
            ← Change number
          </button>
          <button type="button" className="btn btn-ghost" disabled={busy || wait > 0}
                  onClick={() => { void sendCode(); }}>
            {wait > 0 ? `Send a new code in ${wait}s` : 'Send a new code'}
          </button>
        </div>
      </form>
    );
  }

  const ready = name.trim() !== '' && email.trim() !== '' && phone.trim() !== '';
  return (
    <form className="card" style={card}
          onSubmit={(e) => { e.preventDefault(); if (ready) void sendCode(); }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 24 }}>Before we search</h2>
      <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
        Please register to use our free service. Your file is kept exactly as you entered it,
        and the search runs as soon as you are done.{' '}
        {/*
          Bold, and before the boxes, because of what happens without it. Henry, 2026-09-15,
          from the first friends to try the live gate: "otherwise people will try a fake
          phone and be fucked". A broker who types a number they cannot receive a text on
          reaches the code step with nothing to enter and their file behind them.

          It is the one thing said about the code before the code exists - his earlier note
          ("They don't need to know a code is coming til it comes") was about not cluttering
          the screen, and a warning that keeps someone out of a dead end is not clutter.
        */}
        <strong>You will be required to verify your phone number to proceed with a code.</strong>
      </p>
      <label className="lbl" htmlFor="verify-name">Your name</label>
      <input id="verify-name" className="finput" autoComplete="name" autoFocus
             value={name} onChange={(e) => setName(e.target.value)} />
      <label className="lbl" htmlFor="verify-email" style={{ marginTop: 14 }}>Email</label>
      <input id="verify-email" className="finput" type="email" autoComplete="email"
             value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="lbl" htmlFor="verify-phone" style={{ marginTop: 14 }}>Mobile phone</label>
      <input id="verify-phone" className="finput" type="tel" autoComplete="tel"
             placeholder="(310) 555-0100"
             value={phone} onChange={(e) => setPhone(e.target.value)} />
      {error ? <div className="err-text" style={{ marginTop: 10 }}>{error}</div> : null}
      <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 18 }}
              disabled={busy || !ready}>
        {busy ? <><span className="spin" /> &nbsp;Submitting…</> : 'Submit'}
      </button>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 10, marginTop: 14,
      }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          ← Back to the file
        </button>
        <span style={{ fontSize: 12, color: 'var(--faint)' }}>
          US and Canadian numbers. Message and data rates may apply.
        </span>
      </div>
    </form>
  );
}
